import { runInAction } from 'mobx'
import type {
  DebouncePolicy,
  OperationCancellationReason,
  OperationExecutionContext,
  OperationGroup,
  OperationPublication,
  OperationResult,
  PendingPolicy,
  ReadonlyOperationState,
  RetryPolicy,
} from './operation.types'
import { operationCancelled, operationFailed, operationSkipped } from './operation.helpers'

/**
 * Pure per-key execution mechanics (spec §9.2, §10, §11). Holds no MobX state
 * itself — every publicly-visible change goes through `callbacks`, whose
 * implementation (operation.handle.ts) owns the observable state map and the
 * activity ledger used for isBusy/pendingCount (locally and, forwarded, at
 * the OperationRepository level for group/global aggregates).
 *
 * "Unit" = one pending work item this lane currently owns: either the one
 * active execution, or one still-queued `enqueue` call. Each gets a unit id
 * at the moment it starts counting as pending and keeps it until it settles
 * or is cancelled — draining a queued item into the active slot reuses its
 * existing unit id rather than minting a new one, since it was already
 * counted as pending from the moment it was queued.
 */
export interface LaneCallbacks<Failure> {
  readonly onStateChange: (state: ReadonlyOperationState<Failure>) => void
  readonly onUnitStart: (unitId: string, groups: readonly OperationGroup[]) => void
  readonly onUnitEnd: (unitId: string) => void
  readonly getPublishedState: () => ReadonlyOperationState<Failure>
  readonly generateUnitId: () => string
  readonly log: (message: string, context: Record<string, unknown>) => void
  /** Timeout deadlines and retry backoff (spec §12) — never a bare setTimeout. */
  readonly after: (delayMs: number, callback: () => void) => () => void
}

export interface LaneDiagnostics {
  readonly operationId: string
  readonly ownerId: string
}

/** Resolved from `Args` once per logical `run()` call (operation.handle.ts) — Lane never sees `Args` itself. */
export type ResolvedTimeout<Failure> =
  Readonly<{ kind: 'none' }> | Readonly<{ kind: 'after'; milliseconds: number; buildFailure: () => Failure }>

export interface ResolvedExecutionPolicy<Failure> {
  readonly timeout: ResolvedTimeout<Failure>
  readonly retry: RetryPolicy<Failure>
  readonly debounce: DebouncePolicy
  readonly idempotencyKey: string | null
}

type ExecuteFn<Value, Failure> = (context: OperationExecutionContext) => Promise<OperationResult<Value, Failure>>

interface QueuedCall {
  readonly unitId: string
  run(): void
  cancel(reason: OperationCancellationReason): void
}

interface ActiveExecution<Failure> {
  readonly unitId: string
  readonly executionId: string
  readonly abortController: AbortController
  readonly previousStable: ReadonlyOperationState<Failure>
  cancelled: boolean
  cancellationReason: OperationCancellationReason | null
  settleCancelled: (() => void) | null
}

type GuardedWork<Value, Failure> = Promise<
  Readonly<{ kind: 'settled'; result: OperationResult<Value, Failure> }> | Readonly<{ kind: 'thrown'; error: unknown }>
>

function sameGroups(a: readonly OperationGroup[], b: readonly OperationGroup[]): boolean {
  if (a.length !== b.length) return false
  const bSet = new Set(b)
  return a.every((group) => bSet.has(group))
}

export class Lane<Value, Failure> {
  private readonly diagnostics: LaneDiagnostics
  private readonly callbacks: LaneCallbacks<Failure>
  private active: ActiveExecution<Failure> | null = null
  private activePromise: Promise<OperationResult<Value, Failure>> | null = null
  private queue: QueuedCall[] = []
  private currentGroups: readonly OperationGroup[] = []

  constructor(diagnostics: LaneDiagnostics, callbacks: LaneCallbacks<Failure>) {
    this.diagnostics = diagnostics
    this.callbacks = callbacks
  }

  get groups(): readonly OperationGroup[] {
    return this.currentGroups
  }

  get isPending(): boolean {
    return this.active !== null || this.queue.length > 0
  }

  run(
    pending: PendingPolicy,
    execute: ExecuteFn<Value, Failure>,
    publication: OperationPublication<Value, Failure>,
    groups: readonly OperationGroup[],
    policy: ResolvedExecutionPolicy<Failure>,
  ): Promise<OperationResult<Value, Failure>> {
    if (this.active) {
      switch (pending.kind) {
        case 'join': {
          if (!sameGroups(groups, this.currentGroups)) {
            throw new Error(
              `${this.diagnostics.operationId}: a join call resolved a different group set than the in-flight execution.`,
            )
          }
          return this.activePromise!
        }
        case 'ignore':
          return Promise.resolve(operationSkipped('already-pending'))
        case 'replace':
          this.cancelActiveOnly({ kind: 'replaced' })
          return this.startExecution(execute, publication, groups, this.callbacks.generateUnitId(), policy)
        case 'enqueue': {
          if (this.queue.length >= pending.maxQueued) {
            return Promise.resolve(operationSkipped('queue-full'))
          }
          return this.enqueueCall(execute, publication, groups, policy)
        }
      }
    }

    this.currentGroups = groups
    return this.startExecution(execute, publication, groups, this.callbacks.generateUnitId(), policy)
  }

  /** One key/owner/group/all cancellation: restores the previous stable state. */
  cancel(reason: OperationCancellationReason): void {
    // Queue first: cancelActiveOnly auto-drains the next queued item when it
    // sees pending work (the normal settle-and-continue path) — clearing the
    // queue first means there is nothing left to drain into.
    this.cancelQueue(reason)
    this.cancelActiveOnly(reason)
  }

  /** Explicit reset: always ends at `unloaded`, regardless of what was cancelled. */
  resetToUnloaded(reason: OperationCancellationReason): void {
    this.cancelQueue(reason)
    this.cancelActiveOnly(reason)
    runInAction(() => this.callbacks.onStateChange({ status: 'unloaded', error: null }))
  }

  private enqueueCall(
    execute: ExecuteFn<Value, Failure>,
    publication: OperationPublication<Value, Failure>,
    groups: readonly OperationGroup[],
    policy: ResolvedExecutionPolicy<Failure>,
  ): Promise<OperationResult<Value, Failure>> {
    const unitId = this.callbacks.generateUnitId()
    runInAction(() => this.callbacks.onUnitStart(unitId, groups))
    return new Promise((resolve) => {
      this.queue.push({
        unitId,
        run: () => {
          this.startExecution(execute, publication, this.currentGroups, unitId, policy, true).then(resolve)
        },
        cancel: (reason) => {
          runInAction(() => this.callbacks.onUnitEnd(unitId))
          resolve(operationCancelled(reason))
        },
      })
    })
  }

  private cancelQueue(reason: OperationCancellationReason): void {
    if (this.queue.length === 0) return
    const queued = this.queue
    this.queue = []
    queued.forEach((call) => call.cancel(reason))
  }

  private cancelActiveOnly(reason: OperationCancellationReason): void {
    const record = this.active
    if (!record) return

    record.cancelled = true
    record.cancellationReason = reason
    record.abortController.abort()
    record.settleCancelled?.()
    this.active = null
    this.activePromise = null

    runInAction(() => {
      this.callbacks.onUnitEnd(record.unitId)
      if (this.queue.length === 0) this.callbacks.onStateChange(record.previousStable)
    })

    if (this.queue.length > 0) this.drainNext()
  }

  private drainNext(): void {
    const next = this.queue.shift()
    if (!next) return
    next.run()
  }

  private startExecution(
    execute: ExecuteFn<Value, Failure>,
    publication: OperationPublication<Value, Failure>,
    groups: readonly OperationGroup[],
    unitId: string,
    policy: ResolvedExecutionPolicy<Failure>,
    isDrainedFromQueue = false,
  ): Promise<OperationResult<Value, Failure>> {
    const previousStable = this.callbacks.getPublishedState()
    const abortController = new AbortController()
    const executionId = this.callbacks.generateUnitId()
    const record: ActiveExecution<Failure> = {
      unitId,
      executionId,
      abortController,
      previousStable,
      cancelled: false,
      cancellationReason: null,
      settleCancelled: null,
    }

    this.active = record
    this.currentGroups = groups
    runInAction(() => {
      if (!isDrainedFromQueue) this.callbacks.onUnitStart(unitId, groups)
      this.callbacks.onStateChange({ status: 'loading', error: null })
    })

    const promise = this.runOne(record, execute, publication, previousStable, policy)
    this.activePromise = promise
    return promise
  }

  /**
   * One logical execution: an attempt loop racing cancellation, a total
   * timeout deadline (if any), and — between failed attempts — a bounded
   * retry policy's backoff wait. Timeout covers the whole loop including
   * backoff (spec §12: "a total logical deadline, including retry delays"),
   * so one deadline timer is created here, not one per attempt.
   */
  private async runOne(
    record: ActiveExecution<Failure>,
    execute: ExecuteFn<Value, Failure>,
    publication: OperationPublication<Value, Failure>,
    previousStable: ReadonlyOperationState<Failure>,
    policy: ResolvedExecutionPolicy<Failure>,
  ): Promise<OperationResult<Value, Failure>> {
    const cancellationPromise = new Promise<Readonly<{ kind: 'cancelled' }>>((resolve) => {
      record.settleCancelled = () => resolve({ kind: 'cancelled' })
    })

    const timeoutPolicy = policy.timeout
    let deadlineDispose: () => void = () => {}
    let deadlinePromise: Promise<Readonly<{ kind: 'timed-out' }>> | null = null
    let buildTimeoutFailure: (() => Failure) | null = null
    if (timeoutPolicy.kind === 'after') {
      buildTimeoutFailure = timeoutPolicy.buildFailure
      deadlinePromise = new Promise((resolve) => {
        deadlineDispose = this.callbacks.after(timeoutPolicy.milliseconds, () => resolve({ kind: 'timed-out' }))
      })
    }

    try {
      // Debounces the *first* attempt only, before it ever runs — a retry
      // attempt is never itself debounced. Reuses the same race as the retry
      // backoff wait below: a call superseded during this wait was cancelled
      // by the very `replace` that superseded it (Lane.run's 'replace' case
      // calls cancelActiveOnly before starting the new execution), so this
      // falls out of existing mechanics rather than needing its own.
      if (policy.debounce.kind === 'trailing') {
        const debounced = await this.waitForRetryOrInterrupt(
          policy.debounce.milliseconds,
          cancellationPromise,
          deadlinePromise,
        )
        if (debounced.kind === 'cancelled') {
          return operationCancelled(record.cancellationReason ?? { kind: 'manual' })
        }
        if (debounced.kind === 'timed-out') {
          record.abortController.abort()
          return this.publishAndSettle(record, operationFailed(buildTimeoutFailure!()), publication, previousStable)
        }
      }

      let attempt = 1
      for (;;) {
        const context: OperationExecutionContext = {
          signal: record.abortController.signal,
          attempt,
          executionId: record.executionId,
          idempotencyKey: policy.idempotencyKey,
        }

        const guardedWork: GuardedWork<Value, Failure> = execute(context).then(
          (value): Readonly<{ kind: 'settled'; result: OperationResult<Value, Failure> }> =>
            Object.freeze({ kind: 'settled' as const, result: value }),
          (error: unknown): Readonly<{ kind: 'thrown'; error: unknown }> =>
            Object.freeze({ kind: 'thrown' as const, error }),
        )

        const racers: Promise<
          | Readonly<{ kind: 'settled'; result: OperationResult<Value, Failure> }>
          | Readonly<{ kind: 'thrown'; error: unknown }>
          | Readonly<{ kind: 'cancelled' }>
          | Readonly<{ kind: 'timed-out' }>
        >[] = [guardedWork, cancellationPromise]
        if (deadlinePromise) racers.push(deadlinePromise)

        const raced = await Promise.race(racers)

        if (raced.kind === 'cancelled') {
          this.consumeLateSettlement(record, guardedWork)
          return operationCancelled(record.cancellationReason ?? { kind: 'manual' })
        }

        if (raced.kind === 'timed-out') {
          this.consumeLateSettlement(record, guardedWork)
          record.abortController.abort()
          return this.publishAndSettle(record, operationFailed(buildTimeoutFailure!()), publication, previousStable)
        }

        // record.cancelled true here means execute() settled in the same tick as
        // cancellation but the race happened to pick the work branch — treat as
        // cancelled rather than let it commit.
        if (record.cancelled) {
          return operationCancelled(record.cancellationReason ?? { kind: 'manual' })
        }

        if (raced.kind === 'thrown') {
          this.callbacks.log('operation execute() threw', {
            operationId: this.diagnostics.operationId,
            ownerId: this.diagnostics.ownerId,
            executionId: record.executionId,
            attempt,
            error: raced.error,
          })
          this.settleAsUnhandled(record, previousStable)
          throw raced.error
        }

        const result = raced.result
        if (
          result.kind === 'failed' &&
          policy.retry.kind === 'bounded' &&
          attempt < policy.retry.maxAttempts &&
          policy.retry.when(result.error)
        ) {
          const outcome = await this.waitForRetryOrInterrupt(
            policy.retry.delayMilliseconds(attempt),
            cancellationPromise,
            deadlinePromise,
          )
          if (outcome.kind === 'cancelled') {
            return operationCancelled(record.cancellationReason ?? { kind: 'manual' })
          }
          if (outcome.kind === 'timed-out') {
            record.abortController.abort()
            return this.publishAndSettle(record, operationFailed(buildTimeoutFailure!()), publication, previousStable)
          }
          attempt += 1
          continue
        }

        return this.publishAndSettle(record, result, publication, previousStable)
      }
    } finally {
      deadlineDispose()
    }
  }

  /** Races a retry backoff wait against cancellation/deadline, so neither has to wait out a full delay. */
  private waitForRetryOrInterrupt(
    delayMs: number,
    cancellationPromise: Promise<Readonly<{ kind: 'cancelled' }>>,
    deadlinePromise: Promise<Readonly<{ kind: 'timed-out' }>> | null,
  ): Promise<Readonly<{ kind: 'ready' }> | Readonly<{ kind: 'cancelled' }> | Readonly<{ kind: 'timed-out' }>> {
    return new Promise((resolve) => {
      let settled = false
      const dispose = this.callbacks.after(delayMs, () => {
        if (settled) return
        settled = true
        resolve({ kind: 'ready' })
      })
      void cancellationPromise.then(() => {
        if (settled) return
        settled = true
        dispose()
        resolve({ kind: 'cancelled' })
      })
      void deadlinePromise?.then(() => {
        if (settled) return
        settled = true
        dispose()
        resolve({ kind: 'timed-out' })
      })
    })
  }

  /**
   * The real work keeps running after the execution stops waiting for it
   * (cancelled or timed out) — consume its eventual settlement silently so
   * it can never commit and never produces an unhandled rejection.
   */
  private consumeLateSettlement(record: ActiveExecution<Failure>, guardedWork: GuardedWork<Value, Failure>): void {
    void guardedWork.then((settled) => {
      if (settled.kind === 'thrown') {
        this.callbacks.log('operation execute() threw after the execution stopped waiting for it (consumed)', {
          operationId: this.diagnostics.operationId,
          ownerId: this.diagnostics.ownerId,
          executionId: record.executionId,
          error: settled.error,
        })
      }
    })
  }

  /** Programmer-error path: restore state, remove from live counts, never publish a result. */
  private settleAsUnhandled(record: ActiveExecution<Failure>, previousStable: ReadonlyOperationState<Failure>): void {
    if (this.active !== record) return // already superseded; nothing to restore here
    this.active = null
    this.activePromise = null
    runInAction(() => {
      this.callbacks.onUnitEnd(record.unitId)
      if (this.queue.length === 0) this.callbacks.onStateChange(previousStable)
    })
    if (this.queue.length > 0) this.drainNext()
  }

  private publishAndSettle(
    record: ActiveExecution<Failure>,
    result: OperationResult<Value, Failure>,
    publication: OperationPublication<Value, Failure>,
    previousStable: ReadonlyOperationState<Failure>,
  ): OperationResult<Value, Failure> {
    if (this.active !== record) {
      // Superseded between the race resolving and here — never commit.
      return operationCancelled({ kind: 'replaced' })
    }

    const isLast = this.queue.length === 0

    // Publication + the final status snapshot commit in one MobX action —
    // spec §9/§10: a ViewModel can never observe the settled PM without the
    // matching status, or vice versa. A throwing publication callback is a
    // programmer error (spec §10 step 14): restore the pre-cycle state,
    // still remove this execution from live counts, and rethrow — it must
    // not silently become a normal result.
    switch (result.kind) {
      case 'succeeded':
        return this.runPublication(
          record,
          isLast,
          previousStable,
          () => publication.succeeded?.(result.value),
          { status: 'loaded', error: null },
          result,
        )
      case 'failed':
        return this.runPublication(
          record,
          isLast,
          previousStable,
          () => publication.failed?.(result.error),
          { status: 'errored', error: result.error },
          result,
        )
      case 'cancelled':
      case 'skipped': {
        // A nested Repository call inside a Presenter workflow propagated its
        // own control outcome — no publication, restore/keep prior state.
        runInAction(() => this.finishActive(record, isLast, previousStable))
        return result
      }
    }
  }

  private runPublication(
    record: ActiveExecution<Failure>,
    isLast: boolean,
    previousStable: ReadonlyOperationState<Failure>,
    publish: () => void,
    settledState: ReadonlyOperationState<Failure>,
    result: OperationResult<Value, Failure>,
  ): OperationResult<Value, Failure> {
    let publicationError: { readonly error: unknown } | null = null
    runInAction(() => {
      try {
        publish()
        this.finishActive(record, isLast, settledState)
      } catch (error) {
        publicationError = { error }
        this.finishActive(record, isLast, previousStable)
      }
    })
    if (publicationError !== null) {
      this.callbacks.log('operation publication callback threw', {
        operationId: this.diagnostics.operationId,
        ownerId: this.diagnostics.ownerId,
        executionId: record.executionId,
        error: (publicationError as { readonly error: unknown }).error,
      })
      throw (publicationError as { readonly error: unknown }).error
    }
    return result
  }

  private finishActive(
    record: ActiveExecution<Failure>,
    isLast: boolean,
    settledState: ReadonlyOperationState<Failure>,
  ): void {
    this.active = null
    this.activePromise = null
    this.callbacks.onUnitEnd(record.unitId)

    if (!isLast) {
      this.drainNext()
      return
    }

    this.callbacks.onStateChange(settledState)
  }
}
